import React, { useRef, useEffect, useState } from 'react';

const BernieLoading = ({ lineWidth = 16, minLineWidth = 24, maxLineWidth = 24 }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Constrain lineWidth between min and max - default to max for thicker lines
  const actualLineWidth = maxLineWidth;

  // Default size variable - controls line width
  const defaultSize = lineWidth; // px

  // More detailed path coordinates based on the SVG
  const pathSegments = [
    // Main outline path - more detailed
    [
      [348.5, 508], [207, 508], [88, 386.327], [88, 163.5], [11, 84.5],
      [99, 8], [207, 8], [266.5, 67.5], [345.75, 67.5], [425, 67.5],
      [484.5, 8], [589.5, 8], [602.5, 8], [680, 85.5], [607.5, 166.5],
      [605.5, 386], [484.5, 508], [397.5, 416], [345.75, 417.5], [287, 416],
      [229.5, 416], [231.5, 289.5], [88, 386.327]
    ],
    // Additional detail segments
    [
      [88, 386.327], [220, 298], [345.75, 170.5], [345.75, 67.5]
    ],
    [
      [345.75, 170.5], [301.5, 215.366], [393.5, 215.366], [477.066, 125.5],
      [531, 67.5], [484.5, 8]
    ],
    [
      [393.5, 215.366], [345.75, 170.5]
    ],
    [
      [467.5, 298], [393.5, 215.366]
    ],
    [
      [467.5, 298], [467.5, 416], [397.5, 416]
    ],
    [
      [605.5, 386], [467.5, 298]
    ],
    [
      [607.5, 166.5], [562, 212], [524, 80.5], [680, 85.5]
    ],
    [
      [11, 84.5], [168.5, 79.6338], [109, 139.5], [88, 163.5]
    ],
    [
      [99, 8], [213, 125.5], [130.5, 207], [220, 298]
    ],
    [
      [287, 416], [212.5, 503.5]
    ]
  ];

  // Function to flatten all segments into a single array for progress calculation
  const flattenSegments = (segments) => {
    let allPoints = [];
    segments.forEach(segment => {
      allPoints = [...allPoints, ...segment];
    });
    return allPoints;
  };

  const allPoints = flattenSegments(pathSegments);

  useEffect(() => {
    // Handle resize for responsiveness
    const updateDimensions = () => {
      if (containerRef.current) {
        // Ensure dimensions are exactly 24x24px
        setDimensions({ width: 24, height: 24 });
      }
    };

    window.addEventListener('resize', updateDimensions);
    updateDimensions();

    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    if (!canvasRef.current || dimensions.width === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Set canvas dimensions - maintain aspect ratio for smaller height
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;

    // Calculate scale factor to make drawing responsive
    const originalWidth = 691;
    const originalHeight = 516;
    const scaleX = dimensions.width / originalWidth;
    const scaleY = dimensions.height / originalHeight;
    const scale = Math.min(scaleX, scaleY);

    // Calculate centering offset
    const offsetX = (dimensions.width - originalWidth * scale) / 2;
    const offsetY = (dimensions.height - originalHeight * scale) / 2;

    // Duration and timing
    const duration = 6000; // 6 seconds - slower animation
    let startTime = null;
    let lastDrawnPoint = 0;

    // Draw eyes
    const drawEyes = () => {
      ctx.fillStyle = '#071B28';

      // Scale eye radius proportionally to line width
      const eyeRadius = (21.5 / 16) * defaultSize * scale;

      // Left eye
      ctx.beginPath();
      ctx.arc(224.729 * scale + offsetX, 204.5 * scale + offsetY, eyeRadius, 0, Math.PI * 2);
      ctx.fill();

      // Right eye
      ctx.beginPath();
      ctx.arc(465.501 * scale + offsetX, 204.5 * scale + offsetY, eyeRadius, 0, Math.PI * 2);
      ctx.fill();
    };

    // Draw smile - more accurate path based on SVG
    const drawSmile = () => {
      ctx.fillStyle = '#071B28';

      // Create path for the smile, following the exact SVG path
      ctx.beginPath();

      // Starting point
      ctx.moveTo(334.501 * scale + offsetX, 342.54 * scale + offsetY);

      // Top line to corner
      ctx.lineTo(334.501 * scale + offsetX, 325.368 * scale + offsetY);

      // To left point of mouth
      ctx.lineTo(294.812 * scale + offsetX, 285.129 * scale + offsetY);

      // Left curve
      ctx.quadraticCurveTo(286.71 * scale + offsetX, 276.914 * scale + offsetY,
                         292.529 * scale + offsetX, 263 * scale + offsetY);

      // Top line across
      ctx.lineTo(304.068 * scale + offsetX, 263 * scale + offsetY);
      ctx.lineTo(386.161 * scale + offsetX, 263 * scale + offsetY);

      // Right curve
      ctx.quadraticCurveTo(397.7 * scale + offsetX, 263 * scale + offsetY,
                         403.519 * scale + offsetX, 276.914 * scale + offsetY);

      // Right corner
      ctx.quadraticCurveTo(395.417 * scale + offsetX, 285.129 * scale + offsetY,
                         355.729 * scale + offsetX, 325.368 * scale + offsetY);

      // Right vertical line
      ctx.lineTo(355.729 * scale + offsetX, 342.54 * scale + offsetY);

      // Right cheek area
      ctx.quadraticCurveTo(361.968 * scale + offsetX, 344.817 * scale + offsetY,
                         369.69 * scale + offsetX, 346.216 * scale + offsetY);
      ctx.quadraticCurveTo(376.803 * scale + offsetX, 344.98 * scale + offsetY,
                         385.45 * scale + offsetX, 342.403 * scale + offsetY);

      // Right curve details
      ctx.quadraticCurveTo(389.088 * scale + offsetX, 339.142 * scale + offsetY,
                         396.353 * scale + offsetX, 330.667 * scale + offsetY);
      ctx.quadraticCurveTo(398.99 * scale + offsetX, 322.333 * scale + offsetY,
                         412.167 * scale + offsetX, 315.489 * scale + offsetY);

      // Bottom curve back to center
      ctx.quadraticCurveTo(417.696 * scale + offsetX, 328.667 * scale + offsetY,
                         403.107 * scale + offsetX, 354.777 * scale + offsetY);
      ctx.quadraticCurveTo(380.398 * scale + offsetX, 365.67 * scale + offsetY,
                         345.115 * scale + offsetX, 360.916 * scale + offsetY);

      // Left side mirroring
      ctx.quadraticCurveTo(322.882 * scale + offsetX, 367.938 * scale + offsetY,
                         287.122 * scale + offsetX, 354.777 * scale + offsetY);
      ctx.quadraticCurveTo(274.781 * scale + offsetX, 339.933 * scale + offsetY,
                         278.062 * scale + offsetX, 315.489 * scale + offsetY);

      // Left curve details
      ctx.quadraticCurveTo(291.24 * scale + offsetX, 322.333 * scale + offsetY,
                         301.142 * scale + offsetX, 339.142 * scale + offsetY);

      // Left cheek closing
      ctx.quadraticCurveTo(313.426 * scale + offsetX, 344.98 * scale + offsetY,
                         328.262 * scale + offsetX, 344.817 * scale + offsetY);
      ctx.quadraticCurveTo(334.501 * scale + offsetX, 342.54 * scale + offsetY,
                         334.501 * scale + offsetX, 342.54 * scale + offsetY);

      ctx.fill();
    };

    // Animation function
    const animate = (timestamp) => {
      if (!startTime) {
        startTime = timestamp;
        lastDrawnPoint = 0;
      }

      // Calculate progress (0 to 1)
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw the path based on progress
      const totalPoints = allPoints.length;
      const pointsToDraw = Math.floor(progress * totalPoints);

      ctx.strokeStyle = '#071B28';
      ctx.lineWidth = defaultSize * scale;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // Draw each segment
      let drawnPointsCount = 0;

      for (let segmentIndex = 0; segmentIndex < pathSegments.length; segmentIndex++) {
        const segment = pathSegments[segmentIndex];

        if (drawnPointsCount >= pointsToDraw) break;

        ctx.beginPath();
        const firstPoint = segment[0];
        ctx.moveTo(firstPoint[0] * scale + offsetX, firstPoint[1] * scale + offsetY);

        for (let i = 1; i < segment.length && drawnPointsCount + i <= pointsToDraw; i++) {
          const point = segment[i];
          ctx.lineTo(point[0] * scale + offsetX, point[1] * scale + offsetY);
        }

        ctx.stroke();
        drawnPointsCount += segment.length;
      }

      // Draw eyes and smile if we're at least halfway through
      if (progress > 0.5) {
        drawEyes();
        drawSmile();
      }

      // Continue animation or restart
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Immediately restart the animation
        setTimeout(() => {
          startTime = null; // Reset the start time for next cycle
          animationRef.current = requestAnimationFrame(animate);
        }, 10); // Small delay to ensure clean restart
      }
    };

    // Start animation
    animationRef.current = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [dimensions]);

  return (
    <div
      ref={containerRef}
      className="w-full flex items-center justify-center"
      style={{
        width: "24px",
        height: "24px",
        backgroundColor: "transparent"
        // Fallback if transparent doesn't work
        // backgroundColor: "rgb(245, 246, 250)"
      }}
    >
      <canvas ref={canvasRef} />
    </div>
  );
};

// Usage example: <BernieLoading lineWidth={16} minLineWidth={24} maxLineWidth={24} />
export default BernieLoading;
